import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHasMoreRevisions, getRequestOffset, getRevertThemeSettingsRequestStatus, getRevisions, getTotalRevisionCount } from '../selectors/revisionsSelectors';
import { getThemePath } from '../selectors/themeSelectors';
import { getHasAccess, getUser } from '../selectors/authSelectors';
import { getHasBufferedChanges } from '../selectors/themeSettingsMetaSelectors';
import { getThemeSettingsLastSave } from '../selectors/themeSettingsSelectors';
import * as ThemeSettingsActions from '../actions/ThemeSettingsActions';
import { buildPreviewUrl } from '../utils/urls';
import { getBusinessUnitId } from '../selectors/appSelectors';
import { isFailed, isSucceeded } from '../utils/requestStatus';
import { DEFAULT_PAGINATION_LIMIT } from '../api/ThemeSettingsClient';
import I18n from 'I18n';
import { getBufferAuthorName } from '../utils/themeSettings';
const useThemeRevisions = (previewDomain, previewPageId, themeVariant, previewKey, templatePreviewKey) => {
  const dispatch = useDispatch();
  const hasMoreRevisions = useSelector(getHasMoreRevisions);
  const requestOffset = useSelector(getRequestOffset);
  const revertThemeSettingsRequestStatus = useSelector(getRevertThemeSettingsRequestStatus);
  const revisions = useSelector(getRevisions);
  const totalRevisionCount = useSelector(getTotalRevisionCount);
  const businessUnitId = useSelector(getBusinessUnitId);
  const themePath = useSelector(getThemePath);
  const hasAccess = useSelector(getHasAccess);
  const hasBufferedChanges = useSelector(getHasBufferedChanges);
  const user = useSelector(getUser);
  const lastSave = useSelector(getThemeSettingsLastSave);
  useEffect(() => {
    //used with universal theme editor in content editor
    if (isSucceeded(revertThemeSettingsRequestStatus)) {
      const baseUrl = window.location.href.replace('/revisions', '');
      window.location = baseUrl;
    }
  }, [revertThemeSettingsRequestStatus]);
  return useMemo(() => {
    const handleLoadMoreRevisions = () => {
      dispatch(ThemeSettingsActions.fetchRevisions(requestOffset));
    };
    const handleChooseRevision = revision => {
      dispatch(ThemeSettingsActions.revertThemeSettings(revision.id));
    };
    const getPreviewUrl = (selectedRevision, isBuffer) => {
      const isCurrentRevision = selectedRevision.id === -1;
      return buildPreviewUrl(previewDomain || '', themePath, previewPageId || 0, undefined, {
        theme_settings_version: isCurrentRevision || isBuffer ? null : selectedRevision.id,
        hs_preview_key: templatePreviewKey,
        preview_key: previewKey,
        theme_variant: themeVariant,
        language: 'en',
        buId: businessUnitId,
        from_buffer: true
      });
    };
    return {
      chooseRevisionError: isFailed(revertThemeSettingsRequestStatus),
      hasMoreRevisions,
      onChooseRevision: handleChooseRevision,
      onLoadMoreRevisions: handleLoadMoreRevisions,
      getPreviewUrl,
      revisions,
      totalRevisionCount,
      paginationLimit: DEFAULT_PAGINATION_LIMIT,
      shouldShowCurrentDraftLabel: true,
      disabled: !hasAccess,
      title: I18n.text('theme-previewer.themeSettings.universal.revisions.title'),
      hasBufferedChanges,
      lastBufferAuthor: getBufferAuthorName(user),
      lastSave: new Date(Number(lastSave))
    };
  }, [dispatch, hasMoreRevisions, requestOffset, revertThemeSettingsRequestStatus, revisions, totalRevisionCount, previewDomain, themePath, templatePreviewKey, businessUnitId, previewPageId, previewKey, themeVariant, hasAccess, hasBufferedChanges, lastSave, user]);
};
export default useThemeRevisions;